
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  HiOutlineArrowUpRight,
  HiOutlineArrowLeft,
  HiOutlineArrowRight,
  HiOutlinePause,
  HiOutlinePlay,
  HiOutlineScale,
} from "react-icons/hi2";

import { HeroImages } from "../../constants";

/* ============================================
   SLIDER CONFIGURATION
============================================ */

const AUTO_PLAY_DELAY = 15000;

const slides = Array.isArray(HeroImages)
  ? HeroImages
  : [];

/* ============================================
   HERO SLIDER
============================================ */

function Slider() {
  const { i18n } = useTranslation();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const language = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en")
    ? "en"
    : "ar";

  const isEnglish = language === "en";

  const totalSlides = slides.length;
  const currentSlide = slides[currentIndex];

  /* ============================================
     TRANSLATIONS
  ============================================ */

  const labels = {
    eyebrow: isEnglish
      ? "AL NOMAN LAW OFFICE"
      : "مؤسسة النعمان للمحاماة والاستشارات القانونية",

    discover: isEnglish
      ? "Discover Our Office"
      : "تعرف على المؤسسة",

    sideLabel: isEnglish
      ? "OUR COMMITMENT"
      : "التزامنا المهني",

    sideTitle: isEnglish
      ? "Legal Knowledge. Clear Vision."
      : "معرفة قانونية ورؤية واضحة",

    sideDescription: isEnglish
      ? "Every legal matter deserves careful attention, professional responsibility, and a thoughtful approach."
      : "كل قضية تستحق الدراسة المتأنية، والاهتمام بالتفاصيل، والعمل القانوني المسؤول.",

    previous: isEnglish
      ? "Previous slide"
      : "الشريحة السابقة",

    next: isEnglish
      ? "Next slide"
      : "الشريحة التالية",

    pause: isEnglish
      ? "Pause slideshow"
      : "إيقاف العرض التلقائي",

    play: isEnglish
      ? "Resume slideshow"
      : "استئناف العرض التلقائي",

    slide: isEnglish
      ? "Show slide"
      : "عرض الشريحة",

    carousel: isEnglish
      ? "Homepage featured slides"
      : "العرض الرئيسي للصفحة الرئيسية",
  };

  /* ============================================
     AUTO PLAY
  ============================================ */

  useEffect(() => {
    if (totalSlides <= 1 || isPaused) return;

    // Respect reduced-motion preferences.
    const prefersReducedMotion =
      window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

    if (prefersReducedMotion) return;

    const interval = window.setInterval(() => {
      setCurrentIndex(
        (previous) => (previous + 1) % totalSlides
      );
    }, AUTO_PLAY_DELAY);

    return () => {
      window.clearInterval(interval);
    };
  }, [currentIndex, isPaused, totalSlides]);

  /* ============================================
     NAVIGATION FUNCTIONS
  ============================================ */

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const nextSlide = () => {
    setCurrentIndex(
      (previous) => (previous + 1) % totalSlides
    );
  };

  const previousSlide = () => {
    setCurrentIndex(
      (previous) =>
        (previous - 1 + totalSlides) % totalSlides
    );
  };

  const toggleAutoPlay = () => {
    setIsPaused((previous) => !previous);
  };

  /* ============================================
     EMPTY STATE
  ============================================ */

  if (!totalSlides || !currentSlide) {
    return null;
  }

  /* ============================================
     SLIDER
  ============================================ */

  return (
    <section
      id="hero"
      role="region"
      aria-roledescription="carousel"
      aria-label={labels.carousel}
      className="
        relative isolate
        w-full overflow-hidden
        bg-[#0B1726]
        text-white
      "
    >

      {/* ======================================
          BACKGROUND SLIDES
      ====================================== */}

      <div
        className="absolute inset-0 z-0"
        aria-hidden="true"
      >
        {slides.map((slide, index) => {
          const isActive = index === currentIndex;

          return (
            <div
              key={slide.id ?? index}
              className={`
                absolute inset-0
                transition-opacity
                duration-[1200ms]
                ease-in-out
                motion-reduce:transition-none

                ${
                  isActive
                    ? "z-10 opacity-100"
                    : "z-0 opacity-0"
                }
              `}
            >
              <img
                src={slide.img}
                alt=""
                loading={
                  index === 0 ? "eager" : "lazy"
                }
                fetchPriority={
                  index === 0 ? "high" : "auto"
                }
                decoding="async"
                className="
                  h-full w-full
                  object-cover object-center
                "
              />
            </div>
          );
        })}
      </div>

      {/* ======================================
          CINEMATIC IMAGE OVERLAYS
      ====================================== */}

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute inset-0 z-10
          bg-[#071522]/45
        "
      />

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute inset-0 z-10
          bg-gradient-to-t
          from-[#071522]/95
          via-[#071522]/65
          to-[#071522]/20
        "
      />

      {/* Subtle decorative light */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute -top-48 end-0 z-10
          h-[500px] w-[500px]
          rounded-full
          bg-white/[0.035]
          blur-3xl
        "
      />

      {/* ======================================
          HERO MAIN CONTENT
      ====================================== */}

      <div
        className="
          container relative z-20
          flex min-h-[660px]
          items-center
          pb-36 pt-20

          sm:min-h-[700px]

          lg:min-h-[760px]
          lg:pb-40
          lg:pt-24
        "
      >
        <div
          className="
            grid w-full
            items-end
            gap-12

            lg:grid-cols-[minmax(0,1fr)_290px]
            xl:gap-20
          "
        >

          {/* ==================================
              SLIDE TEXT
          ================================== */}

          <div className="max-w-[820px]">

            {/* Eyebrow */}
            <div
              className="
                mb-7 flex
                items-center gap-4
              "
            >
              <span
                aria-hidden="true"
                className="
                  h-px w-11
                  shrink-0
                  bg-[#D7B77F]
                "
              />

              <span
                className="
                  eyebrow
                  text-[#D7B77F]
                "
              >
                {labels.eyebrow}
              </span>
            </div>

            {/* Slide title and description */}
            <div
              key={currentIndex}
              aria-live={isPaused ? "polite" : "off"}
              aria-atomic="true"
            >
              <h1
                className="
                  hero-title
                  max-w-[820px]
                  text-white
                  drop-shadow-[0_8px_30px_rgba(0,0,0,0.25)]
                "
              >
                {isEnglish
                  ? currentSlide.titleEn
                  : currentSlide.titleAr}
              </h1>

              {/* Golden accent */}
              <div
                aria-hidden="true"
                className="
                  mt-7 h-[3px] w-16
                  rounded-full
                  bg-[#D7B77F]
                "
              />

              {/* Description */}
              <p
                className="
                  lead mt-7
                  max-w-[650px]
                  text-white/85
                "
              >
                {isEnglish
                  ? currentSlide.subTitleEn
                  : currentSlide.subTitleAr}
              </p>
            </div>

            {/* ==================================
                CALL TO ACTION
            ================================== */}

            <div className="mt-10 flex flex-wrap items-center gap-5">

              <Link
                to="/AboutUs"
                className="
                  group inline-flex
                  min-h-[56px]
                  items-center justify-center
                  gap-5
                  rounded-xl
                  border border-white
                  bg-white
                  px-6 py-3
                  text-[#102031]
                  shadow-[0_12px_35px_rgba(0,0,0,0.13)]
                  transition-all duration-300

                  hover:-translate-y-0.5
                  hover:bg-[#E8EEF2]
                  hover:shadow-[0_18px_45px_rgba(0,0,0,0.20)]

                  focus-visible:outline-none
                  focus-visible:ring-2
                  focus-visible:ring-[#D7B77F]
                  focus-visible:ring-offset-2
                  focus-visible:ring-offset-[#102031]
                "
              >
                <span className="button text-[#102031]">
                  {labels.discover}
                </span>

                <span
                  className="
                    flex h-9 w-9
                    shrink-0 items-center
                    justify-center
                    rounded-lg
                    bg-[#102031]
                    text-white
                    transition-transform
                    duration-300

                    group-hover:-translate-y-0.5
                  "
                >
                  <HiOutlineArrowUpRight
                    size={19}
                    aria-hidden="true"
                    className="directional-arrow"
                  />
                </span>
              </Link>

            </div>

          </div>

          {/* ==================================
              GLASS SIDE CARD
              Desktop only
          ================================== */}

          <div className="hidden lg:block">

            <div
              className="
                relative overflow-hidden
                rounded-2xl
                border border-white/15
                bg-white/[0.07]
                p-7
                text-white
                shadow-[0_20px_60px_rgba(0,0,0,0.12)]
                backdrop-blur-xl
              "
            >
              {/* Icon */}
              <div
                className="
                  mb-8 flex
                  h-13 w-13
                  items-center justify-center
                  rounded-xl
                  border border-[#D7B77F]/30
                  bg-[#D7B77F]/10
                  p-3
                  text-[#D7B77F]
                "
              >
                <HiOutlineScale
                  size={29}
                  aria-hidden="true"
                />
              </div>

              <p className="eyebrow text-[#D7B77F]">
                {labels.sideLabel}
              </p>

              <h2 className="h5 mt-3 text-white">
                {labels.sideTitle}
              </h2>

              <p className="body-2 mt-4 text-white/70">
                {labels.sideDescription}
              </p>

              {/* Card footer */}
              <div
                className="
                  mt-8 flex items-center
                  justify-between
                  border-t border-white/15
                  pt-5
                "
              >
                <span className="caption text-white/55">
               { isEnglish ?  "AL NOMAN": "النعمان" }
                </span>

                <span
                  className="
                    h-px w-12
                    bg-[#D7B77F]/70
                  "
                />
              </div>

            </div>

          </div>

        </div>
      </div>

      {/* ======================================
          BOTTOM GLASS NAVIGATION BAR

          No bottom border radius.
      ====================================== */}

      {totalSlides > 1 && (
        <div
          className="
            absolute inset-x-0 bottom-0
            z-30
            border-t border-white/15
            bg-[#071522]/55
            backdrop-blur-xl
            backdrop-saturate-150
          "
        >
          <div
            className="
              container flex
              min-h-[86px]
              items-center
              justify-between
              gap-5
              py-3
            "
          >

            {/* ==================================
                SLIDE NUMBERS / PAGINATION
            ================================== */}

            <div className="flex min-w-0 items-center gap-4 sm:gap-5">

              {/* Current number */}
              <span
                className="
                  font-kufi text-xl
                  font-bold
                  tabular-nums
                  text-[#D7B77F]
                "
              >
                {String(currentIndex + 1).padStart(
                  2,
                  "0"
                )}
              </span>

              {/* Navigation indicators */}
              <div
                className="
                  flex items-center
                  gap-2
                "
                aria-label={labels.carousel}
              >
                {slides.map((slide, index) => {
                  const isActive =
                    currentIndex === index;

                  return (
                    <button
                      key={slide.id ?? index}
                      type="button"
                      onClick={() => goToSlide(index)}
                      aria-label={`${labels.slide} ${index + 1}`}
                      aria-current={
                        isActive ? "true" : undefined
                      }
                      className="
                        group flex h-10
                        items-center
                        justify-center
                        focus-visible:outline-none
                        focus-visible:ring-2
                        focus-visible:ring-[#D7B77F]
                        focus-visible:ring-offset-2
                        focus-visible:ring-offset-[#071522]
                      "
                    >
                      <span
                        className={`
                          block h-[3px]
                          rounded-full
                          transition-all duration-500

                          ${
                            isActive
                              ? "w-12 bg-[#D7B77F]"
                              : "w-5 bg-white/35 group-hover:bg-white/75 sm:w-7"
                          }
                        `}
                      />
                    </button>
                  );
                })}
              </div>

              {/* Total */}
              <span
                className="
                  hidden font-kufi
                  text-sm
                  tabular-nums
                  text-white/55
                  sm:block
                "
              >
                / {String(totalSlides).padStart(2, "0")}
              </span>

            </div>

            {/* ==================================
                PREVIOUS / PAUSE / NEXT
            ================================== */}

            <div className="flex shrink-0 items-center gap-2">

              {/* Previous */}
              <button
                type="button"
                onClick={previousSlide}
                aria-label={labels.previous}
                title={labels.previous}
                className="
                  flex h-10 w-10
                  items-center justify-center
                  rounded-xl
                  border border-white/15
                  bg-white/[0.06]
                  text-white
                  transition-all duration-300

                  hover:border-white/30
                  hover:bg-white/[0.14]

                  focus-visible:outline-none
                  focus-visible:ring-2
                  focus-visible:ring-[#D7B77F]
                "
              >
                <HiOutlineArrowLeft
                  size={20}
                  aria-hidden="true"
                  className="directional-arrow"
                />
              </button>

              {/* Pause / Play */}
              <button
                type="button"
                onClick={toggleAutoPlay}
                aria-label={
                  isPaused
                    ? labels.play
                    : labels.pause
                }
                title={
                  isPaused
                    ? labels.play
                    : labels.pause
                }
                aria-pressed={isPaused}
                className="
                  flex h-10 w-10
                  items-center justify-center
                  rounded-xl
                  border border-white/15
                  bg-white/[0.06]
                  text-white
                  transition-all duration-300

                  hover:border-white/30
                  hover:bg-white/[0.14]

                  focus-visible:outline-none
                  focus-visible:ring-2
                  focus-visible:ring-[#D7B77F]
                "
              >
                {isPaused ? (
                  <HiOutlinePlay
                    size={19}
                    aria-hidden="true"
                  />
                ) : (
                  <HiOutlinePause
                    size={19}
                    aria-hidden="true"
                  />
                )}
              </button>

              {/* Next */}
              <button
                type="button"
                onClick={nextSlide}
                aria-label={labels.next}
                title={labels.next}
                className="
                  flex h-10 w-10
                  items-center justify-center
                  rounded-xl
                  border border-white/15
                  bg-white/[0.06]
                  text-white
                  transition-all duration-300

                  hover:border-white/30
                  hover:bg-white/[0.14]

                  focus-visible:outline-none
                  focus-visible:ring-2
                  focus-visible:ring-[#D7B77F]
                "
              >
                <HiOutlineArrowRight
                  size={20}
                  aria-hidden="true"
                  className="directional-arrow"
                />
              </button>

            </div>

          </div>
        </div>
      )}

    </section>
  );
}

export default Slider;
